import { Conversation } from "common/types";
import { useCallback, useEffect, useState } from "react";
import { useConversations } from "requests/tanstack/useConversation";
import { useChatStore } from "store/useChatStore";
import { useSelectedDeviceStore } from "store/useSelectedDeviceStore";
import showSnackbar from "utils/showSnackbar";
import { useSearchParams } from "wouter";
import {
  ConversationFilterEnum,
  ConversationPriorityEnum,
  ConversationTypeEnum,
} from "common/enums";
import { mergeWith } from "lodash";

export default function useComponentLogic() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { selectedDevice } = useSelectedDeviceStore();
  const {
    conversations,
    setConversations,
    selectedConversation,
    setSelectedConversation,
    conversationFilter,
    setConversationsCount,
  } = useChatStore();
  const [searchTerm, setSearchTerm] = useState<string>("");

  const {
    data: conversationsInfiniteQueryData,
    isLoading: conversationsLoading,
    error: conversationsFetchError,
    fetchNextPage,
    fetchPreviousPage,
    hasNextPage,
    hasPreviousPage,
    isFetchingNextPage,
    isFetchingPreviousPage,
  } = useConversations({
    searchTerm,
    limit: 15,
    priority:
      conversationFilter !== ConversationFilterEnum.GROUP &&
      conversationFilter !== ConversationFilterEnum.ALL &&
      conversationFilter !== ConversationFilterEnum.NO_PRIORITY
        ? (conversationFilter as unknown as ConversationPriorityEnum)
        : undefined,
    type:
      conversationFilter === ConversationFilterEnum.GROUP
        ? ConversationTypeEnum.GROUP
        : conversationFilter === ConversationFilterEnum.NO_PRIORITY
          ? undefined
          : ConversationTypeEnum.INDIVIDUAL,
  });

  useEffect(() => {
    if (!conversationsLoading) {
      const newConversations = mergeWith(
        {},
        ...conversationsInfiniteQueryData?.pages.flat(),
        (a, b) => (Array.isArray(a) ? a.concat(b) : undefined)
      );

      setConversations(
        (newConversations?.data?.length > 0 && newConversations.data) || []
      );
      setConversationsCount({
        ...newConversations?.meta?.stats,
        [ConversationFilterEnum.NO_PRIORITY]: newConversations?.meta?.total,
      });
    }
    if (conversationsFetchError) {
      console.error(conversationsFetchError);
      showSnackbar(
        `Falha ao buscar suas conversas! ERR. ${conversationsFetchError?.message}`,
        "error",
        3000
      );
      showSnackbar("Revalidando...", "info", 2500);
      setTimeout(() => (window.location.href = "/"), 6000);
    }
  }, [conversationsInfiniteQueryData, conversationsFetchError]);

  const selecteConversation = useCallback(
    (conversation: Conversation) => {
      setSelectedConversation(conversation);
      setSearchParams(
        {
          deviceId: selectedDevice,
          conversationId: conversation?.id,
        },
        { replace: true }
      );
    },
    [searchParams, setSelectedConversation]
  );

  useEffect(() => {
    if (!selectedConversation?.id) {
      setSearchParams({
        deviceId: selectedDevice,
        conversationId: selectedDevice
          ? searchParams.get("conversationId") || selectedConversation?.id
          : undefined,
      });
    }

    if (conversations.length && selectedDevice) {
      if (!selectedConversation?.id && searchParams.get("conversationId")) {
        const filteredConversationByConversationIdParam = conversations.filter(
          (conversation) =>
            conversation.id === searchParams.get("conversationId")
        )[0];
        selecteConversation(filteredConversationByConversationIdParam);
      }
    }
  }, [conversations, selectedDevice]);

  return {
    conversationsLoading,
    selectedConversation,
    selecteConversation,
    searchTerm,
    setSearchTerm,
    fetchNextPage,
    fetchPreviousPage,
    hasNextPage,
    hasPreviousPage,
    isFetchingNextPage,
    isFetchingPreviousPage,
  };
}
